/**
 * The order action on a branded line sheet (KP-4545).
 *
 * plg-go-be decides where an order goes: a mailto to the person whose email
 * the buyer came from, or the brand's website. This only checks that the link
 * is one of those two schemes and, for a mailto, adds the shortlist to the body
 * so the buyer sends style codes and colours instead of retyping them.
 */

import { productColours } from './lineSheetModel'
import { clipRunes } from './feedbackText'

/** Lines of shortlist a mailto carries before it summarises the rest. */
export const MAX_MAILTO_LINES = 40

/** The CTA's href when it is a mailto (email) or https (website), else "". */
export function safeCtaHref(cta) {
  if (!cta || typeof cta.href !== 'string') return ''
  if (cta.kind === 'email' && cta.href.startsWith('mailto:')) return cta.href
  if (cta.kind === 'website') {
    try {
      return new URL(cta.href).protocol === 'https:' ? cta.href : ''
    } catch (e) {
      return ''
    }
  }
  return ''
}

function itemLine(product) {
  const code = product.styleCode ? ` (${product.styleCode})` : ''
  const colours = productColours(product).map(c => c.name).join(', ')
  return `- ${product.name || 'Style'}${code}${colours ? `, ${colours}` : ''}`
}

/** Characters of the sheet note a mailto carries. */
export const MAX_MAILTO_NOTE_CHARS = 600

// A list of style lines within the remaining budget, with a count of the rest.
function styleBlock(products, budget, moreLabel) {
  const shown = products.slice(0, Math.max(budget, 0)).map(itemLine)
  const more = products.length - shown.length
  return [...shown, ...(more > 0 ? [`and ${more} more ${moreLabel}`] : [])]
}

function section(lines, heading) {
  return lines.length ? [...heading, ...lines, ''] : []
}

function orderBody({
  products, interested, note, pageUrl,
}) {
  const shortlist = styleBlock(products, MAX_MAILTO_LINES, 'on my shortlist')
  const interest = styleBlock(interested, MAX_MAILTO_LINES - Math.min(products.length, MAX_MAILTO_LINES), 'I am interested in')
  const noteText = clipRunes(String(note || '').trim(), MAX_MAILTO_NOTE_CHARS)
  return [
    'Hello,',
    '',
    ...section(shortlist, ['I would like to order these styles from the line sheet:', '']),
    ...section(interest, ['Interested:']),
    ...section(noteText ? [noteText] : [], ['Note:']),
    `Line sheet: ${pageUrl}`,
  ].join('\n')
}

/**
 * The href the order button opens: for a mailto, the shortlist, the styles
 * marked interested and the sheet note (KP-4572) in the body.
 */
export function orderHref({
  cta, products = [], interested = [], note = '', pageUrl = '',
}) {
  const href = safeCtaHref(cta)
  const hasBody = products.length || interested.length || String(note || '').trim()
  if (!href || cta.kind !== 'email' || !hasBody) return href
  const joiner = href.includes('?') ? '&' : '?'
  return `${href}${joiner}body=${encodeURIComponent(orderBody({
    products, interested, note, pageUrl,
  }))}`
}
